/**
 * Branch Table Component
 * Advanced table component for displaying and managing branches
 */

import React from "react";
import Icon from "../../../components/icon/icon";
import Table from "../../../components/table/table";
import { ITEMS_PER_PAGE_OPTIONS } from "../../../constants/pagination";
import { TableColumn } from "../../../types";
import { Badge } from "../../../components/ui/badge";
import { Branch, BranchTableProps } from "../types/branch.types";
import {
  formatBranchLocation,
  formatBranchContact,
  getBranchStatusText,
} from "../utils/branch-utils";

/**
 * Branch table with comprehensive actions and display
 */
export const BranchTable: React.FC<BranchTableProps> = ({
  branches,
  isLoading,
  currentPage,
  itemsPerPage,
  totalPages,
  onEdit,
  onDelete,
  onViewStatistics,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const columns: TableColumn<Branch>[] = [
    {
      title: "Название",
      dataIndex: "name",
      key: "name",
      render: (_value: string, record: Branch) => (
        <div className="branches__name-cell">
          <strong>{record.name}</strong>
          {record.isMainBranch && (
            <Badge variant="info" className="branches__main-badge">
              Главный
            </Badge>
          )}
        </div>
      ),
    },
    {
      title: "Местоположение",
      dataIndex: "city",
      key: "location",
      render: (_value: string, record: Branch) => (
        <div className="branches__location-cell">
          {formatBranchLocation(record)}
        </div>
      ),
    },
    {
      title: "Контакты",
      dataIndex: "phone",
      key: "contact",
      render: (_value: string, record: Branch) => (
        <div className="branches__contact-cell">
          {formatBranchContact(record)}
        </div>
      ),
    },
    {
      title: "Статус",
      dataIndex: "isActive",
      key: "status",
      render: (value: boolean) => (
        <Badge variant={value ? "success" : "error"}>
          {getBranchStatusText(value)}
        </Badge>
      ),
    },
    {
      title: "Действия",
      key: "action",
      dataIndex: "id",
      render: (_, record) => (
        <div className="table-actions">
          <button
            className="table-action-btn"
            onClick={() => onEdit(record)}
            title="Edit"
          >
            <Icon name="pencil" size="small" />
          </button>
          <button
            className="table-action-btn"
            onClick={() => onViewStatistics?.(record)}
            title="View Statistics"
          >
            <Icon name="chart" size="small" />
          </button>
          <button
            className="table-action-btn"
            onClick={() => onDelete(record)}
            title="Delete"
          >
            <Icon name="delete" size="small" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="branches__table-container">
      <Table
        columns={columns}
        dataSource={branches}
        rowKey="id"
        loading={isLoading}
        emptyMessage="Филиалы не найдены"
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
      />

      <div className="branches__pagination">
        <div className="branches__pagination-info">
          Показано{" "}
          {Math.min((currentPage - 1) * itemsPerPage + 1, branches.length)} -{" "}
          {Math.min(currentPage * itemsPerPage, branches.length)} из{" "}
          {branches.length}
        </div>

        <div className="branches__pagination-controls">
          <select
            value={itemsPerPage}
            onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
            className="branches__pagination-select"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} на странице
              </option>
            ))}
          </select>

          <div className="branches__page-buttons">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="branches__page-btn"
            >
              <Icon name="left" size="small" />
            </button>

            <span className="branches__page-info">
              {currentPage} из {totalPages}
            </span>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="branches__page-btn"
            >
              <Icon name="right" size="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
